// ============================================
// VERIFY EMAIL PAGE
// ============================================

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { authApi } from '@/services/api';
import { useEffect, useState } from 'react';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/verify-email/$token' as any)({
  component: VerifyEmailPage,
});

function VerifyEmailPage() {
  const { t } = useTranslation('auth');
  const { token } = Route.useParams() as { token: string };
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');

  const verifyEmailMutation = useMutation({
    mutationFn: (token: string) => authApi.verifyEmail({ token }),
    onSuccess: () => {
      setStatus('success');
      setTimeout(() => {
        navigate({ to: '/login' });
      }, 3000);
    },
    onError: () => {
      setStatus('error');
    },
  });

  useEffect(() => {
    if (token) {
      verifyEmailMutation.mutate(token);
    }
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-maritime-600 to-maritime-800 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm mb-4">
            <img src="/favicon.svg" alt="Crew At Sea" className="w-10 h-10 brightness-0 invert" />
          </div>
          <h1 className="text-3xl font-bold text-white">Crew At Sea</h1>
          <p className="text-maritime-200 mt-1">{t('panelSubtitle.company')}</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {status === 'loading' && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-maritime-100 mb-4">
                <Loader2 className="w-8 h-8 text-maritime-600 animate-spin" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('verifyEmail.loadingTitle')}</h2>
              <p className="text-gray-600">
                {t('verifyEmail.loadingMessage')}
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('verifyEmail.successTitle')}</h2>
              <p className="text-gray-600 mb-6">
                {t('verifyEmail.successMessage')}
              </p>
              <Link to="/login" className="btn-primary w-full">
                {t('verifyEmail.loginNow')}
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('verifyEmail.errorTitle')}</h2>
              <p className="text-gray-600 mb-6">
                {t('verifyEmail.errorMessage')}
              </p>
              <Link to="/login" className="btn-primary w-full">
                {t('verifyEmail.goToLogin')}
              </Link>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-maritime-200 text-sm mt-8">
          {t('footer.copyright', { year: 2025 })}
        </p>
      </div>
    </div>
  );
}
